import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useStore } from 'Store';
import { getStandardLanguageCode } from 'Utils/itemUtils';
import { t } from 'Locale/index';

const defaultTrackModel = { playerValues: [] };

export const LanguageSettingsContextProvider = ({ children }) => {
    const { appManager } = useStore();
    const setSubtitleTrackData = useRef(defaultTrackModel);
    const setAudioTrackData = useRef(defaultTrackModel);
    const [selectedPlayerAudioId, setSelectedPlayerAudioId] = useState(null);
    const [selectedPlayerTextId, setSelectedPlayerTextId] = useState(null);
    const languageLabels = useRef(null);

    const getAudioTrackLabel = (track) => {
        const { audioLanguages } = languageLabels.current;
        const audioLanguage = audioLanguages.find((audio) =>
            audio.playerValues.includes(track.language.toLowerCase())
        );
        return audioLanguage?.name || track.language.toUpperCase();
    };

    const getTextTrackLabel = (track) => {
        const { subtitleLanguages } = languageLabels.current;
        const subtitleLanguage = subtitleLanguages.find((subtitle) =>
            subtitle.playerValues.includes(track.language.toLowerCase())
        );
        return subtitleLanguage?.name || track.language.toUpperCase();
    };

    const getPreferredLangSettings = async () => {
        const languageSettings = (await appManager.getApi().fetchLanguage()) || {
            preferredAudioLanguage: null,
            preferredSubtitlesLanguage: null
        };
        const audioLanguages = (await appManager.getApi().fetchAudioLanguages()) || [];
        const subtitleLanguages = (await appManager.getApi().fetchSubtitleLanguages()) || [];
        return { languageSettings, audioLanguages, subtitleLanguages };
    };

    const setPreferredLangSettings = async (
        languageSettings,
        audioLanguages = [],
        subtitleLanguages = []
    ) => {
        const { preferredAudioLanguage, preferredSubtitlesLanguage } = languageSettings;

        const subtitleTrackData = subtitleLanguages.find(
            (lang) => lang.name === preferredSubtitlesLanguage
        );
        const audioTrackData = audioLanguages.find((lang) => lang.name === preferredAudioLanguage);

        setAudioTrackData.current = audioTrackData || defaultTrackModel;

        if (!subtitleLanguages.length) return;
        setSubtitleTrackData.current = subtitleTrackData || defaultTrackModel;
    };

    const getPreferredLanguageSettings = ({ textTracks, audioTracks }) => {
        // try {
        let setSubtitleTrack;
        let setAudioTrack;
        if (textTracks) {
            setSubtitleTrack = textTracks.find(
                (textTrack) =>
                    !!setSubtitleTrackData.current.playerValues.find(
                        (playerValue) => textTrack.language === playerValue
                        // getStandardLanguageCode(textTrack.language) === playerValue
                    )
            );
        }
        if (audioTracks) {
            setAudioTrack = audioTracks.find(
                (audioTrack) =>
                    !!setAudioTrackData.current.playerValues.find(
                        (playerValue) => audioTrack.language === playerValue
                        // getStandardLanguageCode(audioTrack.language) === playerValue
                    )
            );
        }
        return { subtitleTrack: setSubtitleTrack, audioTrack: setAudioTrack };
        // } catch (error) {
        //     console.log(error);
        // }

        // return { subtitleTrack: undefined, audioTrack: undefined };
    };

    const setSubtitleLang = async (textTrack) => {
        if (!textTrack) {
            setSubtitleTrackData.current = defaultTrackModel;
            return;
        }
        const subtitleLanguages = (await appManager.getApi().fetchSubtitleLanguages()) || [];
        if (subtitleLanguages?.length) {
            const subtitleTrackData = subtitleLanguages.find(({ playerValues }) =>
                playerValues.find((lng) => getStandardLanguageCode(textTrack.language) === lng)
            );
            setSubtitleTrackData.current = subtitleTrackData || defaultTrackModel;
        }
    };

    useEffect(() => {
        const initialize = async () => {
            try {
                languageLabels.current = await getPreferredLangSettings();
                const { languageSettings, audioLanguages, subtitleLanguages } =
                    languageLabels.current;
                setPreferredLangSettings(languageSettings, audioLanguages);
            } catch (error) {
                console.log('Could not set preferred language settings', error);
            }
        };
        initialize();
    }, []);

    return (
        <LanguageSettingsContext.Provider
            value={{
                getPreferredLanguageSettings,
                setPreferredLangSettings,
                setSubtitleLang,
                selectedPlayerAudioId,
                setSelectedPlayerAudioId,
                selectedPlayerTextId,
                setSelectedPlayerTextId,
                getAudioTrackLabel,
                getTextTrackLabel
            }}
        >
            {children}
        </LanguageSettingsContext.Provider>
    );
};

LanguageSettingsContextProvider.defaultProps = {
    children: []
};

LanguageSettingsContextProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

export const LanguageSettingsContext = createContext({});

export const useLanguageSettingsContext = () => useContext(LanguageSettingsContext);

export const withLanguageSettings = (Component) => (props) => (
    <LanguageSettingsContext.Consumer>
        {(context) => <Component {...props} {...context} />}
    </LanguageSettingsContext.Consumer>
);
